/**
 * Frame profiles. Design doc §10.3 (D6, D6b locked).
 *
 * A profile is (quality, orientation, fps). Every variant of a profile is encoded with
 * identical parameters so playlists concatenate with `-c copy`.
 *
 * Bitrates are the LOW-bitrate values chosen for egress savings (D6b). They are to be
 * reviewed after the Phase 3 test streams. Change them here only (and update the doc).
 */
import type { Fps, Orientation, Quality } from "./enums";

export interface Profile {
  quality: Quality;
  orientation: Orientation;
  fps: Fps;
}

export type ProfileKey = `${Quality}-${Orientation}-${Fps}`;

export const profileKey = (p: Profile): ProfileKey => `${p.quality}-${p.orientation}-${p.fps}`;

export interface Canvas {
  w: number;
  h: number;
}

export const CANVAS: Record<Quality, Record<Orientation, Canvas>> = {
  hd: { landscape: { w: 1920, h: 1080 }, portrait: { w: 1080, h: 1920 } },
  "4k": { landscape: { w: 3840, h: 2160 }, portrait: { w: 2160, h: 3840 } },
};

export const QUALITY_RANK: Record<Quality, number> = { hd: 1, "4k": 2 };

/** Fixed audio profile for every plan (D2): AAC-LC, 48 kHz, stereo, 192 kbps. */
export const AUDIO = { codec: "aac", kbps: 192, sampleRate: 48_000, channels: 2 } as const;

export interface ProfileSpec {
  /** Constant video bitrate, kbps. */
  videoKbps: number;
  /** H.264 level (Facebook: 4.1 for 1080p30, 4.2 for 1080p60). */
  h264Level: string;
}

/** Provisional low-bitrate values (D6b). Keyed by quality-fps; portrait uses the same numbers. */
export const PROFILE_SPECS: Record<`${Quality}-${Fps}`, ProfileSpec> = {
  "hd-30": { videoKbps: 6_000, h264Level: "4.1" },
  "hd-60": { videoKbps: 9_000, h264Level: "4.2" },
  "4k-30": { videoKbps: 20_000, h264Level: "5.1" },
  "4k-60": { videoKbps: 30_000, h264Level: "5.2" },
};

export const KEYFRAME_SECONDS = 2;

export function specFor(p: Pick<Profile, "quality" | "fps">): ProfileSpec {
  return PROFILE_SPECS[`${p.quality}-${p.fps}`];
}

/** Keyframe interval in frames (2 s). */
export function gopFrames(fps: Fps): number {
  return KEYFRAME_SECONDS * fps;
}

export function canvasFor(p: Pick<Profile, "quality" | "orientation">): Canvas {
  return CANVAS[p.quality][p.orientation];
}

/** Bandwidth a running stream reserves on a worker (§11.5). 15% headroom over the nominal bitrate. */
export function reservedMbps(p: Pick<Profile, "quality" | "fps">): number {
  return Math.ceil(((specFor(p).videoKbps + AUDIO.kbps) * 1.15) / 1000);
}

/** Approximate egress for one stream running 30 days, in TB (decimal). */
export function egressTbPerMonth(p: Pick<Profile, "quality" | "fps">): number {
  const mbps = (specFor(p).videoKbps + AUDIO.kbps) / 1000;
  return (mbps * 30 * 24 * 3600) / 8 / 1_000_000;
}

export function orientationOf(width: number, height: number): Orientation {
  return height > width ? "portrait" : "landscape"; // square counts as landscape
}

/**
 * Scale factor to fit a source inside the canvas without stretching or cropping.
 * > 1 means the source would have to be upscaled.
 */
export function fitScale(src: { w: number; h: number }, canvas: Canvas): number {
  return Math.min(canvas.w / src.w, canvas.h / src.h);
}

/** Source validation limits at probe time (§10.1). */
export const SOURCE_LIMITS = { minShortEdge: 360, maxLongEdge: 7680 } as const;

/** 4K variants are only produced when the source needs at most this much upscaling (§10.3). */
export const MAX_4K_UPSCALE = 1.05;

/** 60 fps variants need a source at least this fast (§10.3). */
export const MIN_SOURCE_FPS_FOR_60 = 50;
